import { useEffect, useState } from 'react';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, Stack, Switch } from '@mui/material';
import { useNotify, useTranslate } from 'react-admin';
import { AccessDenied } from '@/components/shell/AccessDenied';
import { ApiRequestError } from '@/api/errors';
import { payApiClient } from '@/api/client';
import { TagsField } from '@/features/operations/filters/TagsField';
import type { AdminApiCredential } from '@/api/models';

interface CredentialScopeDialogProps {
  open: boolean;
  credential: AdminApiCredential;
  onClose: () => void;
  onSaved: (credential: AdminApiCredential) => void;
}

/** `PUT /v1/admin/api-credentials/{id}/allowed-tenants` — replaces `allowAllTenants`/`allowedTenantCodes` in full, never a merge. */
export function CredentialScopeDialog({ open, credential, onClose, onSaved }: CredentialScopeDialogProps) {
  const translate = useTranslate();
  const notify = useNotify();
  const [allowAllTenants, setAllowAllTenants] = useState(credential.allowAllTenants);
  const [allowedTenantCodes, setAllowedTenantCodes] = useState<string[]>(credential.allowedTenantCodes);
  const [accessDenied, setAccessDenied] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setAllowAllTenants(credential.allowAllTenants);
      setAllowedTenantCodes(credential.allowedTenantCodes);
      setAccessDenied(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  async function handleSubmit() {
    setSubmitting(true);
    try {
      const updated = await payApiClient.updateAdminApiCredentialAllowedTenants(credential.id, {
        allowAllTenants,
        allowedTenantCodes: allowAllTenants ? [] : allowedTenantCodes,
      });
      notify(translate('pay.credentials.scopeDialog.success'), { type: 'success' });
      onSaved(updated);
    } catch (caught) {
      if (caught instanceof ApiRequestError && caught.errorCode === 'access_denied') {
        setAccessDenied(true);
      } else {
        const message = caught instanceof ApiRequestError ? caught.message : undefined;
        notify(message ?? translate('pay.errorState.body'), { type: 'error' });
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{translate('pay.credentials.scopeDialog.title')}</DialogTitle>
      <DialogContent>
        {accessDenied ? (
          <AccessDenied />
        ) : (
          <Stack spacing={2.5} sx={{ pt: 1 }}>
            <FormControlLabel
              control={<Switch checked={allowAllTenants} onChange={(event) => setAllowAllTenants(event.target.checked)} />}
              label={translate('pay.credentials.scopeDialog.fieldAllowAllTenants')}
            />
            {!allowAllTenants && (
              <TagsField
                label={translate('pay.credentials.scopeDialog.fieldAllowedTenantCodes')}
                helperText={translate('pay.credentials.scopeDialog.fieldAllowedTenantCodesHelp')}
                values={allowedTenantCodes}
                onChange={setAllowedTenantCodes}
              />
            )}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{translate('pay.credentials.scopeDialog.cancel')}</Button>
        {!accessDenied && (
          <Button variant="contained" onClick={handleSubmit} disabled={submitting}>
            {translate('pay.credentials.scopeDialog.submit')}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
